import { useState, useRef, useCallback } from 'react';
import api from 'api';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import '../assets/css/auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const passwordRef = useRef(null);
  const history = useHistory();

  const handleSubmit = useCallback((e) => {
    if (e) e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      toast.error('Please enter your email and password');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);

    const trustedDeviceToken = localStorage.getItem('trustedDeviceToken') || undefined;
    const userData = trustedDeviceToken
      ? { email: trimmedEmail, password, trustedDeviceToken }
      : { email: trimmedEmail, password };

    api('post', '/auth/login', userData)
      .then((res) => {
        if (res.token) {
          setLoading(false);
          const { user } = res;
          localStorage.setItem('token', res.token);
          if (user?.role === 'admin') {
            window.location = '/admin/index';
          } else if (user?.role === 'brand') {
            window.location = '/brand/index';
          }
        } else {
          toast.success(res?.message);
          setLoading(false);
          history.push(`/auth/verify-otp/${email}`);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, [email, password, history]);

  const handleForgotPassword = useCallback((e) => {
    if (e) e.preventDefault();
    const trimmedForgot = forgotEmail.trim().toLowerCase();
    if (!trimmedForgot) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedForgot)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setForgotLoading(true);
    api('put', `/auth/forgot/${encodeURIComponent(trimmedForgot)}`)
      .then(() => {
        setShowForgot(false);
        setForgotEmail('');
        toast.success('Reset link sent! Check your email.');
        setForgotLoading(false);
      })
      .catch(() => {
        setForgotLoading(false);
      });
  }, [forgotEmail]);

  return (
    <>
      <div className="auth-card">
        <div className="auth-card__header">
          <div className="auth-card__logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <h1 className="auth-card__title">Welcome back</h1>
          <p className="auth-card__subtitle">Sign in to your VeriScan brand portal</p>
        </div>

        <div className="auth-card__body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="auth-field">
              <label className="auth-label" htmlFor="login-email">
                Email address
              </label>
              <div className="auth-input-wrap">
                <i className="ni ni-email-83 auth-input-wrap__icon" />
                <input
                  id="login-email"
                  className="auth-input"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="login-password">
                Password
              </label>
              <div className="auth-input-wrap">
                <i className="ni ni-lock-circle-open auth-input-wrap__icon" />
                <input
                  id="login-password"
                  ref={passwordRef}
                  className="auth-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                />
                <button
                  type="button"
                  className="auth-input-wrap__toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'} />
                </button>
              </div>
            </div>

            <div className="auth-remember">
              <label>
                <input type="checkbox" id="remember-me" name="remember" defaultChecked />
                Remember me
              </label>
              <button
                type="button"
                className="auth-forgot"
                onClick={() => {
                  setForgotEmail(email);
                  setShowForgot(true);
                }}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className="auth-btn auth-btn--primary"
              disabled={loading}
            >
              {loading ? <span className="auth-btn__spinner" /> : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <p className="auth-footer-text">
        Don't have an account?{' '}
        <a href="https://getveriscan.com/request-a-demo/" target="_blank" rel="noopener noreferrer">
          Request a demo
        </a>
      </p>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="auth-modal-overlay" onClick={() => setShowForgot(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal__header">
              <h2 className="auth-modal__title">Reset password</h2>
              <button className="auth-modal__close" onClick={() => setShowForgot(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleForgotPassword}>
              <div className="auth-modal__body">
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: '#8898aa', margin: '0 0 16px' }}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <div className="auth-field" style={{ marginBottom: 0 }}>
                  <div className="auth-input-wrap">
                    <i className="ni ni-email-83 auth-input-wrap__icon" />
                    <input
                      className="auth-input"
                      type="email"
                      name="forgot-email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="you@company.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
              </div>
              <div className="auth-modal__actions">
                <button
                  type="button"
                  className="auth-btn auth-btn--secondary"
                  onClick={() => setShowForgot(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="auth-btn auth-btn--primary"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? <span className="auth-btn__spinner" /> : 'Send link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
